/**
 * Column order of a grid (decision Q14: like sort / filter / grouping it is kept for the session
 * only, never persisted). An order is a list of column keys; it is always reconciled with the
 * columns that currently exist, so optional columns can come and go.
 */

/**
 * Effective key order: keys of `order` that still exist (in that order), then every other current
 * key in its natural position order. The result is always a permutation of `keys`.
 */
export function reconcileOrder(order: readonly string[], keys: readonly string[]): string[] {
  const present = new Set(keys);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of order) {
    if (present.has(k) && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  for (const k of keys) {
    if (!seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

/** Columns sorted by `order` (see {@link reconcileOrder}). */
export function orderColumns<C extends { key: string }>(
  columns: readonly C[],
  order: readonly string[],
): C[] {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  return reconcileOrder(
    order,
    columns.map((c) => c.key),
  ).map((k) => byKey.get(k)!);
}

/** Moves the entry at `from` to index `to` (clamped into the list). Out-of-range `from` = no-op. */
export function moveColumn(order: readonly string[], from: number, to: number): string[] {
  const out = [...order];
  if (from < 0 || from >= out.length) return out;
  const target = Math.min(Math.max(to, 0), out.length - 1);
  const [key] = out.splice(from, 1);
  out.splice(target, 0, key!);
  return out;
}

/** Moves `key` by `delta` positions (keyboard: Alt+Shift+←/→). Unknown keys are a no-op. */
export function moveColumnBy(order: readonly string[], key: string, delta: number): string[] {
  const from = order.indexOf(key);
  return from < 0 ? [...order] : moveColumn(order, from, from + delta);
}

/**
 * Drop index for a drag: the insertion slot (0…n) the pointer is nearest to, given the left edges
 * and widths of the rendered columns, converted to the index the dragged column ends up at.
 */
export function dropTarget(
  from: number,
  pointerX: number,
  columns: readonly { left: number; width: number }[],
): number {
  let slot = columns.length;
  for (let i = 0; i < columns.length; i++) {
    const c = columns[i]!;
    if (pointerX < c.left + c.width / 2) {
      slot = i;
      break;
    }
  }
  return slot > from ? slot - 1 : slot;
}

/** True if `order` equals the natural column order (nothing to reset). */
export function isNaturalOrder(order: readonly string[], keys: readonly string[]): boolean {
  const eff = reconcileOrder(order, keys);
  return eff.every((k, i) => k === keys[i]);
}

/** Session-only column order per table id. */
const sessionOrders = new Map<string, readonly string[]>();

export function sessionColumnOrder(id: string): readonly string[] {
  return sessionOrders.get(id) ?? [];
}

export function saveColumnOrder(id: string, order: readonly string[]): void {
  if (order.length === 0) sessionOrders.delete(id);
  else sessionOrders.set(id, [...order]);
}
